import { useCallback, useEffect, useState } from 'react'
import { TIMING } from './constants'

const TOOLBAR_COLLAPSED_KEY = 'collapsible-toolbar-collapsed'

/**
 * Get initial toolbar collapsed state from localStorage
 * Handles SSR safely by checking for window
 */
function getInitialCollapsedState(): boolean {
	if (typeof window === 'undefined') {
		return false
	}

	try {
		const savedState = localStorage.getItem(TOOLBAR_COLLAPSED_KEY)
		if (savedState !== null) {
			return JSON.parse(savedState)
		}
	} catch (error) {
		console.warn('Failed to load toolbar collapsed state from localStorage:', error)
	}

	return false
}

export interface UseToolbarCollapseOptions {
	canCollapse?: boolean
}

export interface UseToolbarCollapseReturn {
	isCollapsed: boolean
	isAnimating: boolean
	showContent: boolean
	collapse: () => void
	expand: () => void
}

/**
 * Hook for managing collapsible toolbar state
 * Handles collapse/expand animations and timing
 * Persists collapsed state to localStorage to survive navigation
 * @param options - Configuration options including canCollapse
 */
export function useToolbarCollapse(
	options: UseToolbarCollapseOptions = {}
): UseToolbarCollapseReturn {
	const { canCollapse = true } = options

	// Use lazy initializer to only read from localStorage once
	// If canCollapse is false, always start expanded
	const [isCollapsed, setIsCollapsed] = useState(() =>
		canCollapse ? getInitialCollapsedState() : false
	)
	const [isAnimating, setIsAnimating] = useState(false)
	const [showContent, setShowContent] = useState(() =>
		canCollapse ? !getInitialCollapsedState() : true
	)

	// Save state to localStorage whenever it changes (only if canCollapse is true)
	useEffect(() => {
		if (!canCollapse) return

		try {
			localStorage.setItem(TOOLBAR_COLLAPSED_KEY, JSON.stringify(isCollapsed))
		} catch (error) {
			console.warn('Failed to save toolbar collapsed state to localStorage:', error)
		}
	}, [isCollapsed, canCollapse])

	const collapse = useCallback(() => {
		if (!canCollapse) return

		setShowContent(false)
		setIsAnimating(true)
		setTimeout(() => {
			setIsCollapsed(true)
			setIsAnimating(false)
		}, TIMING.COLLAPSE_MS)
	}, [canCollapse])

	const expand = useCallback(() => {
		if (!canCollapse) return

		setIsCollapsed(false)
		setShowContent(true) // Set immediately so it doesn't replay collapse animation
		setIsAnimating(true)
		// Wait for the longest animation to complete
		setTimeout(() => {
			setIsAnimating(false)
		}, TIMING.EXPAND_MS)
	}, [canCollapse])

	return { isCollapsed, isAnimating, showContent, collapse, expand }
}
